import {
  Box, Button, Chip, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Input, InputAdornment, Paper, Stack, Tooltip, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { capitalize } from "lodash";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { Loader } from "../../components/CustomLoader";
import { fDateTime } from "../../utils/formatTime";
import { TrimText } from "../../utils/helpers";
import { ADMIN_ROLES, DELETION_REQUEST_STATUS_OPTIONS } from "../../utils/constants";

// Same roles the backend allows to approve or reject.
const REVIEWER_ROLES = [ADMIN_ROLES.SUPER_ADMIN, ADMIN_ROLES.MGT];
const STATUS_COLORS = { pending: "warning", approved: "success", rejected: "error" };
const CLOSED_DIALOG = { open: false, status: null, request: null };

const TABLE_STYLES = {
  headCells: {
    style: {
      fontSize: "15px",
      fontWeight: "bold",
      backgroundColor: "rgba(255, 255, 255, 0.02)",
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
      color: "black",
    },
  },
  rows: { style: { backgroundColor: "transparent", color: "white" } },
  cells: { style: { color: "white" } },
};

function StatusChip({ status }) {
  return <Chip label={capitalize(status)} color={STATUS_COLORS[status]} size="small" />;
}

export default function DeletionRequestList() {
  const user = useSelector((state) => state.auth.user);
  const canReview = REVIEWER_ROLES.includes(user?.role);

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState(["pending"]);
  const [searchItem, setSearchItem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refresh, setRefresh] = useState(false);
  // Flipping this tells the table to go back to page 1.
  const [resetPage, setResetPage] = useState(false);
  const [dialog, setDialog] = useState(CLOSED_DIALOG);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const res = await userServices.getDeletionRequests({
        searchItem,
        pageNumber: currentPage,
        pageSize,
        status,
      });
      setData(res?.data?.data?.result || []);
      setTotalCount(res?.data?.data?.totalRecords || 0);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Waits a little so the search does not run on every key press.
  useEffect(() => {
    const timer = setTimeout(fetchRequests, 400);
    return () => clearTimeout(timer);
  }, [currentPage, pageSize, status, searchItem, refresh]);

  const goToFirstPage = () => {
    setCurrentPage(1);
    setResetPage((prev) => !prev);
  };

  const handleSearchChange = (value) => {
    setSearchItem(value);
    goToFirstPage();
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    goToFirstPage();
  };

  const handlePageSizeChange = (size, page) => {
    setPageSize(size);
    setCurrentPage(page);
  };

  const confirmReview = async () => {
    setIsSaving(true);
    try {
      const res = await userServices.updateDeletionRequest({
        requestId: dialog.request._id,
        status: dialog.status,
      });
      toast.success(res?.data?.message);
      setDialog(CLOSED_DIALOG);
      setRefresh((prev) => !prev);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsSaving(false);
    }
  };

  const renderActions = (row) => {
    if (row.status !== "pending") {
      return <Typography variant="body2" sx={{ color: "text.disabled" }}>-</Typography>;
    }

    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title="Approve">
          <IconButton color="success" onClick={() => setDialog({ open: true, status: "approved", request: row })}>
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reject">
          <IconButton color="error" onClick={() => setDialog({ open: true, status: "rejected", request: row })}>
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const columns = [
    { name: "S.No.", cell: (_, i) => (currentPage - 1) * pageSize + i + 1, width: "80px" },
    { name: "Username", selector: (row) => TrimText(row.userId?.username || "-", 25) },
    { name: "Email", selector: (row) => row.userId?.email || "-", grow: 2 },
    { name: "Requested At", selector: (row) => fDateTime(row.createdAt) },
    { name: "Status", cell: (row) => <StatusChip status={row.status} /> },
    { name: "Reviewed By", selector: (row) => row.reviewedBy?.name || "-" },
    { name: "Reviewed At", selector: (row) => fDateTime(row.reviewedAt) || "-" },
    ...(canReview ? [{ name: "Action", center: true, cell: renderActions }] : []),
  ];

  const isApprove = dialog.status === "approved";

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Helmet><title>User Deletion Requests</title></Helmet>
      <Stack spacing={4}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            background: "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
            User Deletion Requests
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
            Approve a request to make the user inactive. Reject it to keep the user active.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", gap: 2 }}>
            <TableFilter
              data={DELETION_REQUEST_STATUS_OPTIONS}
              value={status}
              setValue={handleStatusChange}
              filterName="Status"
            />
            <Input
              value={searchItem}
              placeholder="Search by username or email..."
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ color: "white", minWidth: 280 }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label="clear-search" onClick={() => handleSearchChange("")}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}
        >
          <DataTable
            customStyles={TABLE_STYLES}
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationDefaultPage={1}
            paginationResetDefaultPage={resetPage}
            onChangePage={setCurrentPage}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 20, 50]}
            onChangeRowsPerPage={handlePageSizeChange}
            progressPending={isLoading}
            progressComponent={<Loader />}
            noDataComponent={<Box sx={{ py: 4 }}>No deletion requests found</Box>}
            className="custom"
          />
        </Paper>
      </Stack>

      <Dialog open={dialog.open} onClose={() => setDialog(CLOSED_DIALOG)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: isApprove ? "success.main" : "error.main", color: "white" }}>
          {isApprove ? "Approve Deletion Request" : "Reject Deletion Request"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography><strong>Username:</strong> {dialog.request?.userId?.username || "-"}</Typography>
          <Typography><strong>Email:</strong> {dialog.request?.userId?.email || "-"}</Typography>
          <Typography sx={{ mt: 2 }}>
            {isApprove
              ? "The user will become inactive and can no longer log in."
              : "The user will stay active. They can send a new request later."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(CLOSED_DIALOG)}>Cancel</Button>
          <Button
            variant="contained"
            color={isApprove ? "success" : "error"}
            onClick={confirmReview}
            disabled={isSaving}
          >
            {isApprove ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
